import { useEffect, useRef, useState } from "react";

import Button from "@/design-system/button";
import Avatar from "@/design-system/icons/avatar-icon";
import Coupon from "@/design-system/icons/coupon.svg";
import TelemetryButton from "@/design-system/telemetry-button";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector, useUserAuthenticationContext, useViewportInfoContext } from "@/src/contexts";
import { useLocationContext } from "@/src/contexts/LocationContext";
import LocationModal from "@/src/location/LocationModal";
import { useMutationCustom } from "@/src/query-client-overrides";
import { Loading } from "@Components/Loading";
import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type PropsWithStyleX,
    colors,
    genericStyles,
} from "../../../styles/tokens.stylex";
import UserContextResources from "./Menu.Resources";
import UserContextResourcesEn from "./Menu.Resources.En";
import MenuButtons from "./menu-buttons";

const SignOut = "/api/account/sign-out";
const MyFavorites = "/ulubione";
const Profile = "/ustawienia/profil";

interface IUserContextState {
    showMenu: boolean;
    showLocationModal: boolean;
}

const defaultState: IUserContextState = {
    showMenu: false,
    showLocationModal: false,
};

export default function Menu({ styleX }: PropsWithStyleX) {
    const resourcesData = languageSelector(UserContextResources, UserContextResourcesEn);
    const resources = (key: keyof typeof resourcesData) => resourcesData[key];
    const { isMobile } = useViewportInfoContext();
    const { location } = useLocationContext();
    const { isAuthenticated, genderType, userName, refreshUserContext } = useUserAuthenticationContext();
    const refMenuDiv = useRef<HTMLDivElement>(null);

    const [state, setState] = useState<IUserContextState>({
        ...defaultState,
    });

    const hideMenu = () => setState((currentState) => ({ ...currentState, showMenu: false }));

    const closeMenuByEvents = (event: Event) => {
        if (refMenuDiv?.current?.contains(event.target as Node)) {
            return;
        }

        if (state.showMenu) {
            hideMenu();
        }
    };

    useEffect(() => {
        const passiveOptions = { passive: true };
        document.addEventListener("mousedown", closeMenuByEvents, passiveOptions);
        document.addEventListener("touchstart", closeMenuByEvents, passiveOptions);
        document.addEventListener("focusin", closeMenuByEvents, passiveOptions);

        return () => {
            document.removeEventListener("mousedown", closeMenuByEvents);
            document.removeEventListener("touchstart", closeMenuByEvents);
            document.removeEventListener("focusin", closeMenuByEvents);
        };
    }, []);

    const { isPending, mutate } = useMutationCustom({
        mutationFn: () => fetch(SignOut).then(refreshUserContext).catch(hideMenu),
    });

    const menuExtraInfo = (label: string, labelTestId: string, extra: string, extraTestId: string) => (
        <div>
            <p {...stylex.props(styles.extraLabel)} data-testid={labelTestId}>
                {label}
            </p>
            <p {...stylex.props(styles.extraInfo)} data-testid={extraTestId}>
                {extra}
            </p>
        </div>
    );

    const guestMenus = [
        <TelemetryButton
            key="menu-sign-in"
            id="menu-sign-in"
            styleX={styles.guestMenuBtn}
            eventName="common-login"
            onClick={hideMenu}
            as="a"
            size="M"
            href="#zaloguj"
            variant="magentaRound"
            data-test-id="hdr-login"
        >
            {resources("loginText")}
        </TelemetryButton>,

        <TelemetryButton
            key="menu-registration"
            id="menu-registration"
            styleX={styles.guestMenuBtn}
            eventName="common-registerIn"
            onClick={hideMenu}
            as="a"
            size="M"
            href="#rejestracja"
            variant="blue"
            data-test-id="hdr-rgr"
        >
            {resources("registerText")}
        </TelemetryButton>,
    ];

    const loggedMenus = [
        <TelemetryButton
            key="menu-profile"
            id="menu-profile"
            styleX={styles.linkOverride}
            href={Profile}
            as="a"
            size="M"
            eventName="btmbar-userProfileTab"
            onClick={hideMenu}
            icon={<Avatar genderType={genderType} styleX={styles.avatar} data-testid="hdr-log-prof-icn" />}
            data-testid="hdr-log-prof-btn"
        >
            {menuExtraInfo(
                resources("myProfile"),
                "hdr-log-prof-name",
                userName || "",
                "hdr-log-prof-user-name",
            )}
        </TelemetryButton>,

        <TelemetryButton
            key="menu-location"
            id="menu-location"
            size="M"
            styleX={styles.linkOverride}
            eventName="btmbar-myLocation"
            onClick={() =>
                setState((currentState) => ({
                    ...currentState,
                    showLocationModal: true,
                    showMenu: false,
                }))
            }
            as="a"
            href="#location"
            data-testid="hdr-log-loca-btn"
        >
            {menuExtraInfo(
                resources("localization"),
                "hdr-log-loca-name",
                location?.placeDescription,
                "hdr-log-loca-city",
            )}
        </TelemetryButton>,

        <TelemetryButton
            key="menu-favorites"
            id="menu-favorites"
            styleX={styles.linkOverride}
            as="a"
            size="M"
            href={MyFavorites}
            eventName="btmbar-favoritesTab"
            data-testid="hdr-log-fav"
        >
            {resources("favorites")}
        </TelemetryButton>,

        isPending ? (
            <Loading key="menu-sign-out" />
        ) : (
            <Button
                as="button"
                key="menu-sign-out"
                id="menu-sign-out"
                styleX={styles.linkOverride}
                onClick={() => mutate()}
                data-testid="hdr-log-logout"
                size="M"
            >
                {resources("signOut")}
            </Button>
        ),
    ];

    useEffect(() => {
        if (state.showMenu) {
            sendTelemetryEvent({
                eventName: "common-MyAccount",
                actionType: "Click",
            });
        }
    }, [state.showMenu]);

    const getMenu = () =>
        (isAuthenticated || isMobile) && (
            <MenuButtons
                menusList={isAuthenticated ? loggedMenus : guestMenus}
                isAuthenticated={isAuthenticated}
                containerRef={refMenuDiv}
                resources={resources}
                show={state.showMenu}
                isMobile={isMobile}
                accountMenuTitle={resources("yourAccountMenu")}
                onOpen={() =>
                    setState((currentState) => ({
                        ...currentState,
                        showMenu: true,
                    }))
                }
                onClose={hideMenu}
            />
        );

    return (
        <div {...stylex.props(styles.container, styleX)}>
            <div {...stylex.props(styles.userAccount, isMobile && styles.userAccountMobile)}>
                {getMenu()}
                <TelemetryButton
                    as="a"
                    href="/kody"
                    variant="none"
                    size="None"
                    isExternal
                    styleX={styles.codeButton}
                    icon={!isMobile && <img src={Coupon} alt="Kody" height={22} width={22} />}
                    eventName="common-VoucherClick"
                    data-testid="hdr-rdmcode"
                >
                    {resources("code")}
                </TelemetryButton>
                {!isAuthenticated && !isMobile && (
                    <div {...stylex.props(styles.contextMenuContainer)}>
                        <TelemetryButton
                            styleX={[styles.contextButton]}
                            eventName="common-login"
                            as="a"
                            size="M"
                            href="#zaloguj"
                            variant="secondary"
                            data-testid="hdr-login"
                        >
                            {resources("loginTextMain")}
                        </TelemetryButton>
                        <TelemetryButton
                            styleX={[styles.contextButton]}
                            eventName="common-registerIn"
                            as="a"
                            size="M"
                            href="#rejestracja"
                            variant="magentaRound"
                            data-testid="hdr-rgr"
                        >
                            {resources("registerTextMain")}
                        </TelemetryButton>
                    </div>
                )}
            </div>
            {state.showLocationModal && (
                <LocationModal onClose={() => setState((s) => ({ ...s, showLocationModal: false }))} />
            )}
        </div>
    );
}

const styles = stylex.create({
    container: {
        fontWeight: 300,

        position: "relative",
        height: "100%",
    },

    avatar: {
        height: "50px",
        width: "50px",
    },

    contextMenuContainer: {
        position: "relative",

        "::before": {
            marginBlock: "auto",

            marginInline: "0",
            content: "",
            position: "absolute",
            borderLeftColor: "#d5d5d5",
            borderLeftStyle: "solid",
            borderLeftWidth: "1px",
            bottom: "0",
            height: "150%",
            left: "-20px",
            top: "0",
        },
    },

    contextButton: {
        paddingBlock: ".5em",
        paddingInline: "1em",
        fontWeight: 400,
        lineHeight: 1.2,
        marginRight: { default: null, ":first-child": "10px" },
    },

    guestMenuBtn: { textAlign: "center" },

    linkOverride: {
        padding: ComposableSpacing.s2,
        borderRadius: genericStyles.borderRadiusS1,
        backgroundColor: { default: null, ":hover": "#f8f8f8", ":focus": "#f8f8f8" },
        color: colors.b_BrandNavy,
        fontWeight: "bold",
        textAlign: "left",
        width: "100%",
    },

    extraLabel: {
        color: "inherit",
    },

    extraInfo: {
        overflow: "hidden",
        color: colors.fg_Tertiary,
        fontSize: "0.875rem",
        fontWeight: 300,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100px",
    },

    userAccount: {
        gap: "40px",
        alignItems: "center",
        display: "flex",
        justifyContent: "flex-end",
        position: "relative",
        textAlign: "center",
        textWrap: "nowrap",
        height: "100%",
        width: "100%",
    },

    userAccountMobile: {
        gap: "10px",
    },

    codeButton: {
        padding: `${ComposableSpacing.s2} 0`,
        gap: ComposableSpacing.s2,
        alignContent: "center",
        color: "#333",
        display: "inline-flex",
        flexWrap: "wrap",
        fontWeight: 300,
    },
});
